<script setup lang="ts">
/**
 * Main services (business sessions) table tab.
 *
 * Infinite-scrolls `/business-services` and joins five reference catalogs:
 * service types, service categories, performers, clients, and groups. The
 * `Service Type` column uses `TwoLineTextCell` (description over category +
 * duration); `Group` shows the session's group name over its age (muted dash
 * for ad-hoc sessions); `Balance` is the attendee men/women split; the
 * `Performers` column stacks gender-colored names; `Revenue` is the session
 * price times the number of attending clients. Row click
 * opens `BusinessServiceCard` (desktop) or `ServiceMobileScreen` (mobile);
 * create/edit is handled through `useAddBusinessServiceFormStore`.
 *
 * Mobile reuses the same `DynamicTable` as desktop (mirrors `ClientsTab`)
 * instead of a bespoke list component. Both desktop and mobile group rows
 * under sticky date headers via `row-override`: `groupedData` splices a
 * synthetic `date-header` row ahead of each date's first session, and
 * `pickRowOverride` renders it as a full-width sticky divider through
 * `DateHeaderRowCell` (shared with `PaymentsTab`) — the same mechanism
 * `ClientCard`'s visits table uses for balance/disease rows. Since the date
 * is shown once in the header, neither desktop nor mobile repeats it per
 * row — desktop keeps only the `Time` column.
 */
import { computed, markRaw, ref, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import AddButton from '@/components/table/AddButton.vue'
import CompactAddButton from '@/components/table/CompactAddButton.vue'
import RowIndexCell from '@/components/table/cell/RowIndexCell.vue'
import TextCell from '@/components/table/cell/TextCell.vue'
import TwoLineTextCell from '@/components/table/cell/TwoLineTextCell.vue'
import GenderBalanceCell from '@/components/table/cell/GenderBalanceCell.vue'
import GenderNameListCell from '@/components/table/cell/GenderNameListCell.vue'
import ServiceGroupCell from '@/components/table/cell/ServiceGroupCell.vue'
import MoneyCell from '@/components/table/cell/MoneyCell.vue'
import ServiceMobileCardCell from '@/components/table/cell/ServiceMobileCardCell.vue'
import DateHeaderRowCell from '@/components/table/cell/DateHeaderRowCell.vue'
import ServiceMobileScreen from '@/components/main/businessServiceCard/ServiceMobileScreen.vue'
import ServiceTypesPane from '@/components/main/tabs/panes/ServiceTypesPane.vue'
import SubscriptionTypesPane from '@/components/main/tabs/panes/SubscriptionTypesPane.vue'
import { useIsVisible } from '@/composables/useIsVisible'
import { useIsMobile } from '@/composables/useIsMobile'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'
import {
  deleteBusinessService,
  fetchBusinessServices,
  type BusinessService,
} from '@/api/businessServices'
import { fetchBusinessServiceTypes, type BusinessServiceType } from '@/api/businessServiceTypes'
import { fetchBusinessServiceCategories } from '@/api/businessServiceCategories'
import { fetchPerformers } from '@/api/performers'
import { fetchGroups } from '@/api/groups'
import { fetchClientsByIds } from '@/api/clients'
import { formatDateTime } from '@/utils/formatDateTime'
import { formatDuration } from '@/utils/formatDuration'
import { formatStartDate } from '@/utils/formatStartDate'
import { useGroupDisplay } from '@/composables/useGroupDisplay'
import { genderBucket, type GenderBucket } from '@/utils/gender'
import {
  businessServicesPollingInterval,
  serviceTypesPollingInterval,
  serviceCategoriesPollingInterval,
  performersPollingInterval,
  groupsPollingInterval,
} from '@/config/api'
import { useBusinessServiceCardStore } from '@/stores/business_service_card_store'
import { useAddBusinessServiceFormStore } from '@/stores/add_business_service_form_store'
import { useAddServiceCategoryFormStore } from '@/stores/add_service_category_form_store'
import { useAddSubscriptionTypeFormStore } from '@/stores/add_subscription_type_form_store'
import { clearInfoViewSelections } from '@/stores/info_view_selection'

const businessServiceCardStore = useBusinessServiceCardStore()
const addBusinessServiceFormStore = useAddBusinessServiceFormStore()
const addServiceCategoryFormStore = useAddServiceCategoryFormStore()
const addSubscriptionTypeFormStore = useAddSubscriptionTypeFormStore()
const { isMobile } = useIsMobile()
const { t } = useI18n()
const { hasRole } = usePermissions()
const { displayName: groupDisplayName } = useGroupDisplay()

type Pane = 'services' | 'types' | 'subscriptions'
const activePane = ref<Pane>('services')

// The Types pane needs the view role of its catalog, so its tab is only offered
// when the viewer holds it. The Subscriptions tab is always shown; without
// `subscription_types_view` the pane explains that instead of querying.
const canViewTypes = computed(() => hasRole(ResourceAccessRole.BusinessServiceTypesView))

// The header's add button follows the active pane: a session, a category (a
// type is added from its category band) or an subscription.
const canAddInPane = computed(() => {
  if (activePane.value === 'services') return hasRole(ResourceAccessRole.BusinessServicesCreate)
  if (activePane.value === 'types') return hasRole(ResourceAccessRole.BusinessServiceNamesCreate)
  return hasRole(ResourceAccessRole.SubscriptionTypesCreate)
})
const addLabel = computed(() => {
  if (activePane.value === 'types') return t('tabs.serviceTypes.addCategory')
  if (activePane.value === 'subscriptions') return t('tabs.subscriptionTypes.addSubscriptionType')
  return t('tabs.services.addService')
})

function onAddInPane(): void {
  if (activePane.value === 'types') {
    clearInfoViewSelections('addServiceCategoryForm')
    addServiceCategoryFormStore.open()
  } else if (activePane.value === 'subscriptions') {
    clearInfoViewSelections('addSubscriptionTypeForm')
    addSubscriptionTypeFormStore.open()
  } else {
    onAddService()
  }
}

const mobileScrollSentinel = ref<HTMLElement | null>(null)
const mobileScrollSentinelVisible = useIsVisible(mobileScrollSentinel)
const isMobileScrolled = computed(() => isMobile.value && !mobileScrollSentinelVisible.value)

interface ServiceRow {
  // Row for the services table, mapped from GET `/business-services`.

  kind: 'service'
  // Discriminant against `ServiceDateHeaderRow` in `groupedData`'s union, so
  // `pickRowOverride` and the mobile column `props()` functions can narrow.

  id: string
  // Business service id (UUID7); powers row selection and the detail card endpoint.

  rowNumber: number
  // 1-based index over all loaded pages (same convention as the other tabs).

  date: string
  // Localized calendar date string (no time of day); groups rows under
  // sticky date headers and becomes `ServiceDateHeaderRow.date`. Not
  // rendered per-row on either desktop or mobile — the header shows it
  // once per group.

  time: string
  // Localized time-of-day string for the `Time` column.

  serviceTypeUpper?: string
  // Upper line of the `Service Type` column: the service type description (e.g. `бачата база`); `undefined` when the API returned a blank description so `TwoLineTextCell` shows only the lower line.

  serviceTypeLower: string
  // Lower line of the `Service Type` column: `<category> <duration>` (mirrors `ServiceTypesPane`'s Name column). Falls back to `Service type #<id>` while the type catalog is still loading or when the id is unknown.

  clientsCount: number
  // Number of clients attached to this service (length of `client_ids`); fed to `PositiveIntegerCell` on desktop.

  menCount: number
  // Number of attached clients resolved to the `men` gender bucket; feeds the mobile list's balance display.

  womenCount: number
  // Number of attached clients resolved to the `women` gender bucket; feeds the mobile list's balance display.

  groupName?: string
  // Display name of the service's group; `undefined` for ad-hoc services and while the group catalog is loading or unreadable (no `GroupsView` role).

  groupDescription?: string
  // Group description (only when the group also has a name, since the name falls back to it); shown after the name in the mobile card.

  groupYears: number
  // Whole years since the group's start date (0 when there is no group).

  groupMonths: number
  // Months (0-11) after `groupYears` since the group's start date.

  revenue: number
  // `price` times `clientsCount`, in major currency units (0 when the service has no price).

  performersDisplay: PerformerDisplay[]
  // Performer id/name/gender for the `Performers` column (desktop and mobile), which color-codes each name by gender. Unknown ids fall back to `Performer #<id>` so rows still render while the performers list is loading.

  _raw: BusinessService
  // Full API record; used by the row edit handler to pre-fill the form.
}

interface PerformerDisplay {
  // Resolved performer display entry for the mobile list's color-coded name.

  id: string
  name: string
  gender: GenderBucket
}

interface ServiceDateHeaderRow {
  // Synthetic full-width divider row spliced into `groupedData` ahead of
  // each date's first session; rendered by the shared `DateHeaderRowCell`
  // via `pickRowOverride`. Shared by both desktop and mobile.

  kind: 'date-header'
  id: string
  date: string
  count: number
  unitKey: string
}

type ServiceListItem = ServiceRow | ServiceDateHeaderRow

// Column widths sum to ~48rem (≈ `--width-middle-column` minus the
// article's 4rem horizontal padding) so this table has the same total
// width as every other tab's table; see `App.vue` for context.
//
// Typed over `ServiceListItem` (not just `ServiceRow`) and each props()
// casts its row, because `groupedData` — fed to both desktop and mobile —
// also contains synthetic `date-header` rows; `rowOverride` guarantees
// props() is never actually invoked for those (mirrors `mobileColumns`
// below). Computed (not a plain const) so labels re-translate when the
// user switches language.
const columns = computed<Column<ServiceListItem>[]>(() => [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: (row as ServiceRow).rowNumber }),
    width: '3rem',
    align: 'center',
  },
  {
    key: 'time',
    // Date is shown once per group in the sticky `DateHeaderRowCell`
    // divider (see `groupedData`), so this column only needs the time.
    label: t('tabs.services.colTime'),
    component: markRaw(TextCell),
    props: (row) => ({ value: (row as ServiceRow).time }),
    width: '4.5rem',
  },
  {
    key: 'serviceType',
    label: t('tabs.services.colServiceType'),
    component: markRaw(TwoLineTextCell),
    props: (row) => {
      const r = row as ServiceRow
      return { title: r.serviceTypeUpper, subtitle: r.serviceTypeLower }
    },
    width: '15rem',
  },
  {
    key: 'group',
    label: t('tabs.services.colGroup'),
    component: markRaw(ServiceGroupCell),
    props: (row) => {
      const r = row as ServiceRow
      return { name: r.groupName, years: r.groupYears, months: r.groupMonths }
    },
    width: '9rem',
  },
  {
    key: 'balance',
    label: t('tabs.services.colBalance'),
    component: markRaw(GenderBalanceCell),
    props: (row) => {
      const r = row as ServiceRow
      return { maleCnt: r.menCount, femaleCnt: r.womenCount, compact: true }
    },
    align: 'center',
    width: '4.5rem',
  },
  {
    key: 'performers',
    label: t('tabs.services.colPerformers'),
    component: markRaw(GenderNameListCell),
    props: (row) => ({ items: (row as ServiceRow).performersDisplay, emptyPlaceholder: '–' }),
    width: '19.5rem',
  },
  {
    key: 'revenue',
    label: t('tabs.services.colRevenue'),
    component: markRaw(MoneyCell),
    props: (row) => ({
      amount: (row as ServiceRow).revenue,
      precision: 0,
      fractionDigits: 2,
      align: 'right',
    }),
    align: 'right',
    width: '6rem',
  },
])

const {
  data: queryData,
  isPending,
  isError,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ['businessServices'],
  queryFn: ({ pageParam }) => fetchBusinessServices(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: businessServicesPollingInterval,
  refetchIntervalInBackground: false,
})

/**
 * Service types are fetched separately to resolve `business_service_type_id`
 * into a human-readable two-line label that mirrors `ServiceTypesPane`'s Name
 * column. The same `queryKey` is shared with `ServiceTypesPane` so the cache is
 * reused and the catalog is fetched at most once per session bucket.
 */
const { data: typesQueryData } = useInfiniteQuery({
  queryKey: ['businessServiceTypes'],
  queryFn: ({ pageParam }) => fetchBusinessServiceTypes(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: serviceTypesPollingInterval,
  refetchIntervalInBackground: false,
  // Enrichment only (resolves the Service Type column's label): a user
  // without this role still sees the tab (gated on BusinessServicesView),
  // just with the existing "Service type #<id>" fallback per row instead
  // of the resolved name.
  enabled: computed(() => hasRole(ResourceAccessRole.BusinessServiceTypesView)),
})

/**
 * Service categories (business service names) provide the human label used as
 * the first segment of the lower line in the `Service Type` column. Cache is
 * shared with `ServiceTypesPane` / `ServiceTypesPane` for the same reason.
 */
const { data: namesQueryData } = useInfiniteQuery({
  queryKey: ['businessServiceCategories'],
  queryFn: ({ pageParam }) => fetchBusinessServiceCategories(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: serviceCategoriesPollingInterval,
  refetchIntervalInBackground: false,
  // Enrichment only — see typesQueryData above; falls back to
  // "Category #<id>" per row without this role.
  enabled: computed(() => hasRole(ResourceAccessRole.BusinessServiceNamesView)),
})

/**
 * Performers are fetched separately so `performers_ids` on each service can be
 * resolved to display names. The list is small (one page in practice) but uses
 * `useInfiniteQuery` to stay consistent with the other reference catalogs and
 * to transparently handle the rare case of multiple pages. Polling keeps the
 * cache fresh on the same cadence as `businessServices` so newly-added
 * performers appear without a manual refresh.
 */
const { data: performersQueryData } = useInfiniteQuery({
  queryKey: ['performers'],
  queryFn: ({ pageParam }) => fetchPerformers(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: performersPollingInterval,
  refetchIntervalInBackground: false,
  // Enrichment only — see typesQueryData above; falls back to
  // "Performer #<id>" per row without this role.
  enabled: computed(() => hasRole(ResourceAccessRole.StuffView)),
})

/**
 * Groups resolve `group_id` into the `Group` column's name and age. Cache is
 * shared with `ClientsTab`'s groups table. Enrichment only: without
 * `GroupsView` the column shows a dash instead of redirecting on a 403.
 */
const { data: groupsQueryData } = useInfiniteQuery({
  queryKey: ['groups'],
  queryFn: ({ pageParam }) => fetchGroups(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) => (lastPage.has_next_page ? lastPage.next_cursor : undefined),
  refetchInterval: groupsPollingInterval,
  refetchIntervalInBackground: false,
  enabled: computed(() => hasRole(ResourceAccessRole.GroupsView)),
})

const groupInfoById = computed(() => {
  const map = new Map<
    string,
    { name: string; description?: string; years: number; months: number }
  >()
  for (const page of groupsQueryData.value?.pages ?? []) {
    for (const g of page.items ?? []) {
      const { duration } = formatStartDate({ date: g.start, timeZone: 'UTC' })
      map.set(g.id, {
        name: groupDisplayName(g),
        description: g.name && g.description?.trim() ? g.description.trim() : undefined,
        years: duration.years,
        months: duration.months,
      })
    }
  }
  return map
})

const categoryNameById = computed<Map<string, string>>(() => {
  const map = new Map<string, string>()
  const pages = namesQueryData.value?.pages
  if (!pages?.length) return map
  for (const page of pages) {
    for (const item of page.items ?? []) {
      map.set(item.id, item.name)
    }
  }
  return map
})

interface ServiceTypeInfo {
  // Compact catalog entry used to render the `Service Type` column.

  type: BusinessServiceType
  // The service type definition (provides description + duration).

  categoryNameId: string
  // Parent category id (UUID7), resolved against `categoryNameById` for the lower line.
}

const serviceTypeById = computed<Map<string, ServiceTypeInfo>>(() => {
  const map = new Map<string, ServiceTypeInfo>()
  const pages = typesQueryData.value?.pages
  if (!pages?.length) return map
  for (const page of pages) {
    for (const group of page.items ?? []) {
      for (const t of group.business_service_types ?? []) {
        map.set(t.id, {
          type: t,
          categoryNameId: group.business_service_name_id,
        })
      }
    }
  }
  return map
})

const performerNameById = computed<Map<string, string>>(() => {
  const map = new Map<string, string>()
  const pages = performersQueryData.value?.pages
  if (!pages?.length) return map
  for (const page of pages) {
    for (const p of page.items ?? []) {
      const name =
        typeof p.name === 'string' && p.name.trim() !== ''
          ? p.name.trim()
          : t('tabs.services.performerFallback', { id: p.id })
      map.set(p.id, name)
    }
  }
  return map
})

const performerGenderById = computed<Map<string, GenderBucket | null>>(() => {
  const map = new Map<string, GenderBucket | null>()
  const pages = performersQueryData.value?.pages
  if (!pages?.length) return map
  for (const page of pages) {
    for (const p of page.items ?? []) {
      map.set(p.id, genderBucket(p.gender))
    }
  }
  return map
})

/**
 * Clients are resolved by id (not paginated in full) so the mobile list can
 * show each service's attendee gender balance (`menCount`/`womenCount`)
 * without loading the entire client roster. Mirrors the same pattern used by
 * `ServiceTypeMobileScreen`'s session history.
 */
const allClientIds = computed<string[]>(() => {
  const ids = new Set<string>()
  const pages = queryData.value?.pages
  if (!pages?.length) return []
  for (const page of pages) {
    for (const s of page.items ?? []) {
      if (Array.isArray(s.client_ids)) for (const id of s.client_ids) ids.add(id)
    }
  }
  return Array.from(ids).sort()
})

const { data: clientsByIdsData } = useQuery({
  queryKey: ['clientsByIds', allClientIds],
  queryFn: () => fetchClientsByIds(allClientIds.value),
  // Enrichment only (mobile gender-count summary) — see typesQueryData
  // above; without ClientsView, menCount/womenCount both stay 0 per row.
  enabled: computed(() => allClientIds.value.length > 0 && hasRole(ResourceAccessRole.ClientsView)),
  staleTime: 0,
  refetchInterval: businessServicesPollingInterval,
  refetchIntervalInBackground: false,
})

const clientGenderById = computed<Map<string, GenderBucket | null>>(() => {
  const map = new Map<string, GenderBucket | null>()
  for (const c of clientsByIdsData.value?.items ?? []) {
    map.set(c.id, genderBucket(c.gender))
  }
  return map
})

const data = computed<ServiceRow[]>(() => {
  const pages = queryData.value?.pages
  if (!pages?.length) return []
  let index = 0
  const rows: ServiceRow[] = []
  for (const page of pages) {
    for (const s of page.items ?? []) {
      index += 1
      const { formattedDate, formattedTime } = formatDateTime({
        date: s.date_time,
      })
      const info = serviceTypeById.value.get(s.business_service_type_id)
      let serviceTypeUpper: string | undefined
      let serviceTypeLower: string
      if (info != null) {
        const descriptionRaw = info.type.description
        serviceTypeUpper =
          typeof descriptionRaw === 'string' && descriptionRaw.trim() !== ''
            ? descriptionRaw.trim()
            : undefined
        const categoryName =
          categoryNameById.value.get(info.categoryNameId) ??
          t('tabs.services.categoryFallback', { id: info.categoryNameId })
        const duration = formatDuration(info.type.duration) ?? undefined
        const lowerParts: string[] = [categoryName]
        if (duration != null) lowerParts.push(duration)
        serviceTypeLower = lowerParts.join(' ')
      } else {
        serviceTypeLower = t('tabs.services.serviceTypeFallback', {
          id: s.business_service_type_id,
        })
      }
      const clientIds = Array.isArray(s.client_ids) ? s.client_ids : []
      let menCount = 0
      let womenCount = 0
      for (const id of clientIds) {
        const gender = clientGenderById.value.get(id)
        if (gender === 'men') menCount++
        else if (gender === 'women') womenCount++
      }
      const performerIds = Array.isArray(s.performers_ids) ? s.performers_ids : []
      const group = s.group_id ? groupInfoById.value.get(s.group_id) : undefined
      const performersDisplay: PerformerDisplay[] = performerIds.map((id) => ({
        id,
        name: performerNameById.value.get(id) ?? t('tabs.services.performerFallback', { id }),
        gender: performerGenderById.value.get(id) ?? 'men',
      }))
      rows.push({
        kind: 'service',
        id: s.id,
        rowNumber: index,
        date: formattedDate,
        time: formattedTime,
        serviceTypeUpper,
        serviceTypeLower,
        clientsCount: clientIds.length,
        menCount,
        womenCount,
        groupName: group?.name,
        groupDescription: group?.description,
        groupYears: group?.years ?? 0,
        groupMonths: group?.months ?? 0,
        revenue: (s.price ?? 0) * clientIds.length,
        performersDisplay,
        _raw: s,
      })
    }
  }
  return rows
})

const mobileColumns: Column<ServiceListItem>[] = [
  {
    key: 'card',
    label: '',
    component: markRaw(ServiceMobileCardCell),
    // Never invoked for `date-header` rows: `rowOverride` makes DynamicTable
    // skip the per-column props() pipeline entirely for those rows.
    props: (row) => {
      const r = row as ServiceRow
      return {
        time: r.time,
        serviceType: r.serviceTypeUpper ?? r.serviceTypeLower,
        groupName: r.groupName,
        groupDescription: r.groupDescription,
        groupYears: r.groupYears,
        groupMonths: r.groupMonths,
        menCount: r.menCount,
        womenCount: r.womenCount,
        revenue: r.revenue,
        performers: r.performersDisplay,
      }
    },
  },
]

/**
 * Shared row list for both desktop and mobile: `data` with a synthetic
 * `date-header` row spliced in ahead of each date's first session. Rows are
 * already grouped contiguously by date (the API returns them in that
 * order), so a single pass comparing against the previous row's date is
 * enough.
 */
const groupedData = computed<ServiceListItem[]>(() => {
  const result: ServiceListItem[] = []
  let currentHeader: ServiceDateHeaderRow | null = null
  for (const row of data.value) {
    if (!currentHeader || currentHeader.date !== row.date) {
      currentHeader = {
        kind: 'date-header',
        id: `date-header:${row.date}`,
        date: row.date,
        count: 0,
        unitKey: 'units.session',
      }
      result.push(currentHeader)
    }
    currentHeader.count += 1
    result.push(row)
  }
  return result
})

/** Selects `DateHeaderRowCell` for synthetic date-header rows so the table
 * renders one full-width sticky divider instead of the normal row cells.
 * Returns `null` for service rows on both desktop and mobile. */
function pickRowOverride(row: unknown): Component | null {
  if ((row as ServiceListItem).kind === 'date-header') return markRaw(DateHeaderRowCell)
  return null
}

function rowKeyFn(row: unknown): string {
  return (row as ServiceListItem).id
}

function onRowClick(row: unknown) {
  clearInfoViewSelections('businessService')
  businessServiceCardStore.selectBusinessService((row as ServiceRow).id)
}

const selectedRowKey = computed<string | null>(
  () => businessServiceCardStore.selectedBusinessServiceId,
)

const headerRef = ref<HTMLElement | null>(null)
const isHeaderVisible = useIsVisible(headerRef)

function onAddService(): void {
  clearInfoViewSelections('addBusinessServiceForm')
  addBusinessServiceFormStore.open()
}

const queryClient = useQueryClient()
const deleteMutation = useMutation({ mutationFn: (id: string) => deleteBusinessService(id) })

function onRowDeleteHandler(row: unknown): void {
  const r = row as ServiceRow
  if (!confirm(t('tabs.services.confirmDelete'))) return
  deleteMutation.mutate(r.id, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['businessServices'] })
      if (businessServiceCardStore.selectedBusinessServiceId === r.id)
        businessServiceCardStore.clearSelection()
    },
  })
}

function onRowEditHandler(row: unknown): void {
  const s = (row as ServiceRow)._raw
  clearInfoViewSelections('addBusinessServiceForm')
  addBusinessServiceFormStore.openForEdit(s.id, {
    serviceTypeId: s.business_service_type_id,
    dateTime: s.date_time,
    performerIds: s.performers_ids,
    clientIds: s.client_ids,
    groupId: s.group_id ?? null,
    description: s.description ?? null,
    price: s.price,
  })
}
</script>

<template>
  <article>
    <div v-if="isMobile" ref="mobileScrollSentinel" class="scroll-sentinel" aria-hidden="true" />
    <div
      ref="headerRef"
      class="tab-header"
      :class="{
        'tab-header--mobile': isMobile,
        'tab-header--mobile-scrolled': isMobileScrolled,
      }"
    >
      <div
        v-if="!isMobileScrolled"
        class="pane-tabs"
        role="group"
        :aria-label="t('tabs.services.paneGroupLabel')"
      >
        <button
          type="button"
          class="pane-tab"
          :class="{ 'pane-tab--active': activePane === 'services', 'pane-tab--mobile': isMobile }"
          @click="activePane = 'services'"
        >
          {{ t('tabs.services.paneServices') }}
        </button>
        <button
          v-if="canViewTypes"
          type="button"
          class="pane-tab"
          :class="{ 'pane-tab--active': activePane === 'types', 'pane-tab--mobile': isMobile }"
          @click="activePane = 'types'"
        >
          {{ t('tabs.services.paneTypes') }}
        </button>
        <button
          type="button"
          class="pane-tab"
          :class="{
            'pane-tab--active': activePane === 'subscriptions',
            'pane-tab--mobile': isMobile,
          }"
          @click="activePane = 'subscriptions'"
        >
          {{ t('tabs.services.paneSubscriptions') }}
        </button>
      </div>
      <AddButton v-if="!isMobile && canAddInPane" :label="addLabel" @click="onAddInPane" />
      <CompactAddButton v-if="isMobile && canAddInPane" :label="addLabel" @click="onAddInPane" />
    </div>
    <ServiceTypesPane v-if="activePane === 'types' && canViewTypes" />
    <SubscriptionTypesPane v-else-if="activePane === 'subscriptions'" />
    <template v-else>
      <p v-if="isPending" class="text-tufte-muted">{{ t('tabs.services.loading') }}</p>
      <p v-else-if="isError" class="text-red-600">
        {{
          t('tabs.services.loadError', {
            message: (error as Error)?.message ?? t('common.unknownError'),
          })
        }}
      </p>
      <template v-else>
        <!-- pinnable-header is always false: sticky date-header rows and a
           pinned <thead> both target `top: 0` on the same scroll container
           and would visually collide; `ClientCard`'s visits table hit this
           same conflict and resolved it the same way. -->
        <DynamicTable
          :class="isMobile ? 'services-table--mobile' : 'services-table'"
          :columns="(isMobile ? mobileColumns : columns) as Column<unknown>[]"
          :data="groupedData"
          :has-next-page="hasNextPage"
          :is-fetching-next-page="isFetchingNextPage"
          :on-load-more="fetchNextPage"
          :on-row-click="onRowClick"
          :row-override="pickRowOverride"
          :row-key="rowKeyFn"
          :selected-row-key="isMobile ? null : selectedRowKey"
          :on-row-edit="
            isMobile || !hasRole(ResourceAccessRole.BusinessServicesEdit)
              ? undefined
              : onRowEditHandler
          "
          :on-row-delete="
            isMobile || !hasRole(ResourceAccessRole.BusinessServicesDelete)
              ? undefined
              : onRowDeleteHandler
          "
          :on-add="
            isMobile || !hasRole(ResourceAccessRole.BusinessServicesCreate)
              ? undefined
              : onAddService
          "
          :pinnable-header="false"
          :compact-add-visible="!isMobile && !isHeaderVisible"
          storage-key="services"
        />
      </template>

      <ServiceMobileScreen
        v-if="isMobile && businessServiceCardStore.hasSelection"
        :business-service-id="businessServiceCardStore.selectedBusinessServiceId!"
        @close="businessServiceCardStore.clearSelection()"
      />
    </template>
  </article>
</template>

<style scoped>
/*
 * The table stretches to fill the middle column (`width: 100%` default) so
 * that extra horizontal space the three-column layout gives the middle
 * column is reflected in the table itself. Columns keep their preferred
 * widths via the `width` values on the column definitions above.
 */

.tab-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.pane-tabs {
  display: flex;
  align-items: baseline;
  gap: 1.2rem;
  /* Three headings must stay on one line; the font size is reduced to fit them. */
  flex-wrap: nowrap;
  min-width: 0;
}

.pane-tab {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-3xl);
  font-weight: 400;
  line-height: var(--line-height-tufte-tight);
  white-space: nowrap;
  color: var(--color-tufte-muted);
}

.pane-tab:hover,
.pane-tab--active {
  color: var(--color-tufte-text);
}

.pane-tab--mobile {
  font-size: var(--text-tufte-mobile-title);
}

.scroll-sentinel {
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
}

.tab-header--mobile {
  padding: 0.75rem 1.25rem;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
}

.tab-header--mobile h1 {
  margin-top: 0;
  margin-bottom: 0;
}

.tab-header--mobile-scrolled {
  padding: 0.4rem 1.25rem;
  justify-content: flex-end;
}

/* Mobile table: full-width, no thead, touch-friendly cell padding (mirrors
 * ClientsTab's mobile table treatment; ServiceDateHeaderRowCell overrides
 * this padding on its own sticky divider row via `!important`). */
.services-table--mobile :deep(.dynamic-table) {
  width: 100%;
}

.services-table--mobile :deep(thead) {
  display: none;
}

.services-table--mobile :deep(td) {
  padding: 0.7rem 1.25rem;
}
</style>
